import React, {useContext, useEffect, useRef, useState} from 'react';
import {DeleteItemDialog} from '../components/dialogs/DeleteDialog/DeleteItemDialog';
import {FileDetailsDialog} from '../components/dialogs/FileDetailsDialog/FileDetailsDialog';
import {NumericValue, StringArrayValue, StringValue, useQueryParam} from '../hooks/UseQuerryParam';
import {Dialog, Layout} from '@bosch/react-frok';
import {SearchBarComponent} from '../components/SearchBar/SearchBar';
import {CreateEditFolderDialog} from '../components/dialogs/CreateEditFolderDialog/CreateEditFolderDialog';
import {AddItemMenu} from '../components/AddItemMenu/AddItemMenu';
import {FolderDetailsDialog} from '../components/dialogs/FolderDetailsDialog/FolderDetailsDialog';
import {ObjectType} from '../models/ObjectType';
import {StorageObject} from '../models/StorageObject';
import {UploadFileDialog} from '../components/dialogs/UploadFileDialog/UploadFileDialog';
import {t} from 'i18next';
import {
    LENGTH_LIMIT_URL_QUERY_PARAMETER,
    NO_DISCLAIMER_KEY,
    PARAM_FILE_ID,
    PARAM_FILE_TYPES,
    PARAM_IS_FAVORITE,
    PARAM_IS_PUBLIC,
    PARAM_LAYOUT,
    PARAM_ORDER,
    PARAM_PAGE,
    PARAM_PATH,
    PARAM_RECENTLY_ADDED,
    PARAM_SEARCH,
    PARAM_SHOW_ITEM_DETAILS,
    PARAM_SHOW_ITEM_PREVIEW,
    PARAM_TYPE,
    WATERMARK
} from '../init/constants';
import {useDeleteItem} from '../hooks/UseDeleteItem';
import {useCreateFolder} from '../hooks/UseCreateFolder';
import {useUploadFile} from '../hooks/UseUploadFile';
import {useEditFolder} from '../hooks/UseEditFolder';
import {useEditFile} from '../hooks/UseEditFile';
import {useGetStorageObjects} from '../hooks/UseGetStorageObjects';
import {EditFileDialog} from '../components/dialogs/UploadFileDialog/EditFileDialog';
import {AuthContext} from '../context/AuthContext';
import {LocaleContext} from '../context/LocaleContext';
import {useDownloadFile} from '../hooks/UseDownloadFile';
import {ItemsFooter} from '../components/itemVisualisation/ItemsFooter/ItemsFooter';
import {ItemsHeader} from '../components/itemVisualisation/ItemsHeader/ItemsHeader';
import {dynamicConfig} from '../init/config';
import {ErrorHint} from '../components/errors/ErrorHint';
import {LoadingIndicator} from '../components/LoadingIndicator/LoadingIndicator';
import {ItemsViewWrapper} from '../components/itemVisualisation/ItemsViewWrapper.style';
import {Breadcrumb, BreadcrumbPaths} from '../components/Breadcrumb/Breadcrumb';
import {
    UploadProgressIndicator,
    uploadProgressIndicatorId
} from '../components/UploadProgressIndicator/UploadProgressIndicator';
import {BreadcrumbsDTO} from '../models/dto/BreadcrumbsDTO';
import {
    convertLocaleToPersistentValue,
    createSearchFilterPredefined,
    getBreadcrumbState,
    isNullOrEmpty,
    isPredefinedFilterApplicable,
    isTrue,
    isValidSearchTerm,
    isWatermarkApplicableCheck
} from '../utils';
import {useSearchParams} from 'react-router-dom';
import {DownloadFileDialog} from '../components/dialogs/DownloadFileDialog/DownloadFileDialog';
import {useGetDisclaimerData} from '../hooks/UseGetDisclaimerData';
import {DisclaimerDTO} from '../models/dto/DisclaimerDTO';
import {useMarkAsFavorite} from '../hooks/UseMarkAsFavorite';
import {useGetLabelData} from '../hooks/UseGetLabelData';
import {ValidationMessage} from '../models/ValidationMessage';
import {SortOrder} from '../models/SortOrder';
import {TYPE_ALL} from '../components/ObjectTypePicker/resources/FileTypeResource';
import {getCreateFolderDTO, getEditFileDTO, getEditFolderDTO, getUploadFileDTO} from '../utils/DtoUtils';
import {hasPublicOnlyAccess} from '../utils/UserUtils';
import {ItemsView} from '../components/itemVisualisation/ItemsView';
import {NotificationType} from '../models/NotificationType';
import {
    getErrorDataValidationMessages,
    getGeneralErrorErrorMessage,
    getNoDataFoundErrorMessage,
    getSearchTermConstraintErrorMessage,
    getUrlLengthLimitConstraintErrorMessage
} from '../components/errors/ErrorRegister';
import {ErrorDialogue, setErrorDescription} from '../components/errors/ErrorDialogue';
import {useGiveConsent} from '../hooks/UseGiveConsent';
import {PreviewDialog} from '../components/dialogs/PreviewDialog/PreviewDialog';
import {generateIdentifier} from '../utils/IdentityUtils';
import {TranscodeService} from '../services/transcode.service';
import {getLabelEntryMapDefault, getLabelNative} from '../utils/StorageObjectUtils';

/**
 * @name DownloadCenter
 * @description
 * Main component of the download center that connects all components and its business logic
 */
declare const window: Window & { dataLayer: Record<string, unknown>[]; };

export const DownloadCenter: React.VFC = () => {
    const [showItemDetails, setShowItemDetails] = useQueryParam(PARAM_SHOW_ITEM_DETAILS, {
        paramType: StringValue,
        defaultValue: null,
        replaceNavigation: false
    });

    const [showPreview, setShowPreview] = useQueryParam(PARAM_SHOW_ITEM_PREVIEW, {
        paramType: StringValue,
        defaultValue: null,
        replaceNavigation: false
    });

    const [fileId, setFileId] = useQueryParam(PARAM_FILE_ID, {
        paramType: StringValue,
        defaultValue: null,
        replaceNavigation: true
    });

    const [path, setPath] = useQueryParam(PARAM_PATH, {
        paramType: StringValue,
        defaultValue: '/',
        replaceNavigation: true
    });

    const [page, setPage] = useQueryParam(PARAM_PAGE, {
        paramType: NumericValue,
        defaultValue: 1,
        replaceNavigation: true
    });

    const [sortOrder, setSortOrder] = useQueryParam(PARAM_ORDER, {
        paramType: StringValue,
        defaultValue: SortOrder.ASC,
        replaceNavigation: true
    });

    const [itemLayout, setItemLayout] = useQueryParam(PARAM_LAYOUT, {
        paramType: StringValue,
        defaultValue: dynamicConfig.DLS_LAYOUT,
        replaceNavigation: true
    });

    const [searchTerm, setSearchTerm] = useQueryParam(PARAM_SEARCH, {
        paramType: StringValue,
        defaultValue: null,
        replaceNavigation: true
    });

    const [fileTypes, setFileTypes] = useQueryParam(PARAM_FILE_TYPES, {
        paramType: StringArrayValue,
        defaultValue: null,
        replaceNavigation: true
    });

    const [noOfDays, setNoOfDays] = useQueryParam(PARAM_RECENTLY_ADDED, {
        paramType: NumericValue,
        defaultValue: 0,
        replaceNavigation: true
    });

    const [isFavorite, setIsFavorite] = useQueryParam(PARAM_IS_FAVORITE, {
        paramType: StringValue,
        defaultValue: 'false',
        replaceNavigation: true
    });

    const [isPublic, setIsPublic] = useQueryParam(PARAM_IS_PUBLIC, {
        paramType: StringValue,
        defaultValue: 'false',
        replaceNavigation: true
    });

    const [objectType, setObjectType] = useQueryParam(PARAM_TYPE, {
        paramType: StringValue,
        defaultValue: '',
        replaceNavigation: true
    });

    const {isAuthenticated, roles, isAuthenticating} = useContext(AuthContext);
    const {language, country} = useContext(LocaleContext);
    const defaultLocale = language.toLowerCase().concat('_').concat(country.toUpperCase());
    const [searchParams, setSearchParams] = useSearchParams();

    const setProgress = (progress: number) => {
        const progressIndicator = document.querySelector<HTMLElement>(`.${uploadProgressIndicatorId} div`);
        if (progressIndicator) {
            const oldProgressValue = progressIndicator.style.width;
            const newProgressValue = `${parseInt(progress.toString())}%`;

            if (newProgressValue !== oldProgressValue) {
                progressIndicator.style.width = newProgressValue;
            }
        }
    };

    const {
        headlessDeleteAction,
        itemToDelete,
        setItemToDelete,
        countriesToDelete,
        setCountriesToDelete,
        languagesToDelete,
        setLanguagesToDelete,
        localesToDelete,
        setLocalesToDelete,
        isCompleteToDelete,
        setIsCompleteToDelete,
        deleteItem,
        cancelDeleteItem
    } = useDeleteItem();

    const {createFolderModeActive, setCreateFolderModeActive, createFolder} = useCreateFolder();
    const {folderToEdit, setFolderToEdit, editFolder} = useEditFolder();
    const {fileToEdit, setFileToEdit, editFile} = useEditFile();
    const {
        uploadFileModeActive,
        setUploadFileModeActive,
        uploadFile,
        isLoading: uploadInProgress
    } = useUploadFile(setProgress);

    const {giveConsent} = useGiveConsent();
    // const {revokeConsent} = useRevokeConsent();

    const {fileToDownload, setFileToDownload, downloadFile} = useDownloadFile();

    const {markAsFavorite} = useMarkAsFavorite();

    const getIsRecentlyAdded = (): boolean => {
        return ((noOfDays !== undefined) && (noOfDays > 0)) as boolean;
    }

    const getIsFavorite = (): boolean => {
        return hasPublicOnlyAccess(isAuthenticated, roles) ? false : isTrue(isFavorite);
    };

    const getIsPublic = (): boolean => {
        return hasPublicOnlyAccess(isAuthenticated, roles) ? false : isTrue(isPublic);
    };

    const {
        data,
        isLoading: loadingStorageObjects,
        isError: loadingStorageObjectsError
    } = useGetStorageObjects(country, language, path, page, sortOrder.toLowerCase(), searchTerm, fileId, fileTypes, noOfDays,
        getIsFavorite(), getIsPublic(), objectType.toLowerCase());

    const {existingLabelData} = useGetLabelData();

    const {
        data: disclaimerData
    } = useGetDisclaimerData();

    // We hold permissions state as an option to direct integration with authorization system like Keycloak
    const [roleDataSource] = useState<string[]>(dynamicConfig.PERMISSIONS.sort());
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbsDTO[]>([]);
    const [breadcrumbPath, setBreadcrumbPath] = useState<BreadcrumbPaths>(BreadcrumbPaths.HOME);
    const [parentOwnerRoles, setParentOwnerRoles] = useState<string[]>([]);
    const [parentRoles, setParentRoles] = useState<string[]>([]);
    const [parentLocales, setParentLocales] = useState<string[]>([]);
    const [disclaimerOptions, setDisclaimerOptions] = useState<DisclaimerDTO[]>([]);
    const [disclaimerDialog, setDisclaimerDialog] = useState<boolean>(false);
    const [disclaimerWatermarkDialog, setDisclaimerWatermarkDialog] = useState<boolean>(false);
    const [storageObjects, setStorageObjects] = useState<StorageObject[]>([]);
    const [favoritesStorageObjects, setFavoritesStorageObjects] = useState<string[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [itemsPerPageCount, setItemsPerPageCount] = useState(0);
    const [editModeActive, setEditModeActive] = useState<boolean>(false);
    const [populateError, setPopulateError] = useState<boolean>(false);
    const [dialogError, setDialogError] = useState<Partial<{ headline?: string, errorDescription?: string }>>();
    const [showProgressDialog, setShowProgressDialog] = useState<boolean>(false);
    const layoutRef = useRef<HTMLDivElement>(null);

    // Sets the default mandatory query parameters to url
    useEffect(() => {
        let pathValue = decodeURI(path);
        if ((path === '/') || (path !== pathValue)) {
            setPath(pathValue);
        }
        setPage(page);
        setSortOrder(sortOrder.toLowerCase());
        setItemLayout(itemLayout.toLowerCase());
        setPopulateError(true);
    }, [path, page, sortOrder, itemLayout]);

    // Sets predefined query parameters to url only once on mount
    useEffect(() => {
        let predefinedFilters = dynamicConfig.PREDEFINED_FILTERS;
        let searchFilterState = createSearchFilterPredefined(searchTerm, fileTypes, objectType);
        if ((path === '/') && (!isPredefinedFilterApplicable(searchFilterState)) && isPredefinedFilterApplicable(predefinedFilters)) {
            setSearchTerm(predefinedFilters.searchParam);
            setFileTypes(predefinedFilters.fileTypes);
            setObjectType(predefinedFilters.objectType);
            setBreadcrumbPath(BreadcrumbPaths.FILTERED);
        }
    }, []);

    useEffect(() => {
        if ((data !== undefined) && data) {
            let totalCount = data.totalCount ? data.totalCount : 0;
            setPageCount((totalCount > 0) ? (data.totalPages ? data.totalPages : 0) : 0);
            setTotalItemsCount(totalCount);
            setItemsPerPageCount((totalCount > 0) ? (data.pageSize ? data.pageSize : 0) : 0);
            setStorageObjects(data.storageObjects ? data.storageObjects : []);
            setFavoritesStorageObjects(data.favoriteStorageObjectIds ? data.favoriteStorageObjectIds : []);
            setBreadcrumbs(data.breadcrumbs ? data.breadcrumbs : []);
            setParentOwnerRoles(data.parentOwnerRoles ? data.parentOwnerRoles : []);
            setParentRoles(data.parentRoles ? data.parentRoles : []);
            setParentLocales(data.parentLocales ? data.parentLocales : []);
        } else {
            setPageCount(0);
            setTotalItemsCount(0);
            setItemsPerPageCount(0);
            setStorageObjects([]);
            setFavoritesStorageObjects([]);
            setBreadcrumbs([]);
            setParentOwnerRoles([]);
            setParentRoles([]);
            setParentLocales([]);
        }
        setPopulateError(true);
    }, [data]);

    useEffect(() => {
        if ((disclaimerData !== undefined) && disclaimerData) {
            setDisclaimerOptions(disclaimerData);
        }
    }, [disclaimerData]);

    useEffect(() => {
        setEditModeActive(roles.includes(dynamicConfig.DLS_EDITOR_ROLE_NAME));
    }, [roles]);

    useEffect(() => {
        setShowProgressDialog(uploadInProgress);
    }, [uploadInProgress]);

    useEffect(() => {
        if ((fileId !== undefined) && fileId) {
            setShowItemDetails(fileId);
        }
    }, [fileId]);

    useEffect(() => {
        // Finds the item from the deep link in case the first attempt to find the item fails(query returns zero results)
        // This can be caused by isFavorite, isPublic, different user role set
        // The trigger of this condition is that the link contains instruction to popup file with id(property showItemDetails)
        if ((data !== undefined) && data) {
            let totalCount = data.totalCount ? data.totalCount : 0;
            if ((page > 1) && (totalCount === 0)) {
                setFileId(showItemDetails);
                setPage(1);
            }

            if ((!isAuthenticated) && (showItemDetails !== undefined) && showItemDetails) {
                setFileId(showItemDetails);
            }
        }
    }, [data, page, showItemDetails, isAuthenticated]);

    const renderErrorDialog = (populate: boolean, headline: string | undefined, description: string | undefined) => {
        return <ErrorDialogue populate={populate}
                              headline={headline}
                              description={description}
                              onChange={(populated: boolean) => {
                                  setPopulateError(populated);
                                  setDialogError(undefined);
                              }}/>
    };

    const navigateToPreviousPage = () => {
        if (!isTrue(isFavorite)) {
            return;
        }

        if ((page > 1) && (data?.totalPages && (data?.totalPages > 1)) && (data?.pageSize && (data?.pageSize > 0))
            && (data?.totalCount && (data?.totalCount === (((page - 1) * data?.pageSize) + 1)))) {
            setPage(page - 1);
        }
    };

    const handleDeleteSearchParameter = (searchParam: string) => {
        if ((searchParam !== undefined) && (!isNullOrEmpty(searchParam))) {
            searchParams.delete(searchParam);
            setSearchParams(searchParams);
        }
    };

    const handleResetSearchParameters = () => {
        setShowItemDetails(undefined);
        setFileId(undefined);
        setItemLayout(undefined);
        setSearchTerm(undefined);
        setFileTypes(undefined);
        setNoOfDays(undefined);
        setIsFavorite(undefined);
        setIsPublic(undefined);
        searchParams.forEach(key => searchParams.delete(key));
        setSearchParams(searchParams);
        setPath('/');
        setPage(1);
        setSortOrder(SortOrder.ASC);
        setObjectType(TYPE_ALL);
    };

    const renderItems = () => {
        const handleShowPreviewSelection = (item: StorageObject) => {
            setShowPreview(item.id);
        };

        const handleItemSelection = (item: StorageObject) => {
            if (item.objectType === ObjectType.FOLDER) {
                setPath(decodeURI(item.fullPath));
                setPage(1);
                layoutRef.current?.scrollIntoView({behavior: 'smooth'});
            } else {
                setShowItemDetails(item.id);
            }
        };

        const handleItemEdit = (item: StorageObject) => {
            if (item.objectType === ObjectType.FOLDER) {
                setFolderToEdit(item);
            } else {
                setFileToEdit(item);
            }
        };

        if (loadingStorageObjectsError) {
            if (!isValidSearchTerm(searchTerm)) {
                let errorData = getErrorDataValidationMessages(t('ERROR_SEARCH_PARAMETER_VALUE_CONSTRAINT_HEADLINE'), getSearchTermConstraintErrorMessage());
                return <ErrorDialogue populate={populateError}
                                      headline={errorData?.headline}
                                      description={errorData?.errorDescription}
                                      onChange={(populated: boolean) => setPopulateError(populated)}/>
            } else if ((data === undefined) || (!data)) {
                let urlLength = window.location.href.length;
                let validationMessages: ValidationMessage[] = [];
                if (urlLength > LENGTH_LIMIT_URL_QUERY_PARAMETER) {
                    validationMessages = getUrlLengthLimitConstraintErrorMessage(urlLength);
                }

                let errorData = getErrorDataValidationMessages(t('ERROR_LOADING_FAILED'), validationMessages);
                return <ErrorDialogue populate={populateError}
                                      headline={errorData?.headline}
                                      description={errorData?.errorDescription}
                                      onChange={(populated: boolean) => setPopulateError(populated)}/>
            } else {
                let errorData = getErrorDataValidationMessages(t('ERROR_LOADING_FAILED'), getGeneralErrorErrorMessage());
                return <ErrorDialogue populate={populateError}
                                      headline={errorData?.headline}
                                      description={errorData?.errorDescription}
                                      onChange={(populated: boolean) => setPopulateError(populated)}/>
            }
        } else if (loadingStorageObjects) {
            return <LoadingIndicator/>;
        } else if ((storageObjects === undefined) || (!storageObjects)) {
            let errorData = getErrorDataValidationMessages(t('ERROR_LOADING_FAILED'), getNoDataFoundErrorMessage());
            return <ErrorDialogue populate={populateError}
                                  headline={errorData?.headline}
                                  description={errorData?.errorDescription}
                                  onChange={(populated: boolean) => setPopulateError(populated)}/>
        }

        return <ItemsView itemLayout={itemLayout}
                          items={storageObjects}
                          favoriteItems={favoritesStorageObjects}
                          language={language}
                          defaultLocale={defaultLocale}
                          editMode={editModeActive}
                          labelData={existingLabelData}
                          onEdit={(item) => handleItemEdit(item)}
                          onDelete={(item) => setItemToDelete(item)}
                          onDownload={(item) => handleDownloadFile(item)}
                          onShowDetails={(item) => setShowItemDetails(item.id)}
                          onMarkAsFavorite={(item, favoriteItem) => handleMarkAsFavorite(item, favoriteItem)}
                          onItemSelect={(item) => handleItemSelection(item)}
                          onShowPreview={(item) => handleShowPreviewSelection(item)}
                          onCopyUrl={(item) => handleCopyUrl(item)}/>;
    };

    const resetFileDownload = () => {
        setDisclaimerDialog(false);
        setDisclaimerWatermarkDialog(false);
        setFileToDownload(null);
    }

    /**
     * Attempts to download a file. Checks if a watermark needs to be added to the file on download.
     * Ask user to give consent and accept disclaimer data.
     * If Watermark service is NOT enabled - downloads the file, else sets a watermarked file for download and
     thus opens a warning that
     * the file will be watermarked on download which allows to confirm to proceed with download
     * @param item a storage object that represent a file to download
     */
    const handleDownloadFile = (item: StorageObject) => {
        if ((item === undefined) || (!item)) {
            return;
        }

        let isSilentDownload = true;
        if (isWatermarkApplicableCheck(item.metadata.watermark)) {
            setFileToDownload(item);
            isSilentDownload = false;
        }

        if (dynamicConfig.DISCLAIMER_ENABLED && (!!item.metadata.disclaimer) && (item.metadata.disclaimer != NO_DISCLAIMER_KEY)
            && disclaimerOptions && (disclaimerOptions.length > 0)) {
            let selectedData: DisclaimerDTO[] = disclaimerOptions?.filter(filteredData => (filteredData.disclaimerKey === item.metadata.disclaimer));
            if (selectedData && (selectedData.length > 0)) {
                setFileToDownload(item);
                isSilentDownload = false;
                if (item.metadata.disclaimer?.includes(WATERMARK)) {
                    setDisclaimerWatermarkDialog(true);
                } else {
                    setDisclaimerDialog(true);
                }
            }
        }

        if (isSilentDownload) {
            executeDownloadFile(item);
        }
    };

    const getCountryDataSource = (locales: string[] | undefined) => {
        let countryDataSource: string[] = [];
        let localeItem: string[] = [];
        if (locales) {
            locales.map(locale => {
                if (locale) {
                    localeItem = locale.split('_');
                    if ((localeItem) && (localeItem.length >= 2)) {
                        countryDataSource.push(localeItem[1].toUpperCase());
                    }
                }
            });
        }

        return countryDataSource;
    };

    const getLanguageDataSource = (locales: string[] | undefined) => {
        let languageDataSource: string[] = [];
        let localeItem: string[] = [];
        if (locales) {
            locales.map(locale => {
                if (locale) {
                    localeItem = locale.split('_');
                    if ((localeItem) && (localeItem.length >= 2)) {
                        languageDataSource.push(localeItem[0].toLowerCase());
                    }
                }
            });
        }

        return languageDataSource;
    };

    const getLocaleDataSource = (locales: string[] | undefined) => {
        let localeDataSource: string[] = [];
        let localeItem: string[] = [];
        if (locales) {
            locales.map(locale => {
                if (locale) {
                    localeItem = locale.split('_');
                    if ((localeItem) && (localeItem.length >= 2)) {
                        localeDataSource.push(convertLocaleToPersistentValue(locale));
                    }
                }
            });
        }

        return localeDataSource;
    };

    const hideFormDetailsDialog = () => {
        setShowItemDetails(undefined);
        setFileId(undefined);
    };

    const hidePreviewDetailsDialog = () => {
        setShowPreview(undefined);
        setFileId(undefined);
    };

    const deleteFromDetailsDialog = (item: StorageObject) => {
        setItemToDelete(item);
        setShowItemDetails(undefined);
    };

    const handleCopyUrl = async (item: StorageObject) => {
        if (item && (!isNullOrEmpty(item.id)) && (!isNullOrEmpty(item.fullPath))) {
            let url = new URL(window.location.href);
            url.search = '';
            url.searchParams.set('path', TranscodeService.decodeString(item.path));
            url.searchParams.set('page', '1');
            url.searchParams.set('order', SortOrder.ASC);
            url.searchParams.set('search', TranscodeService.decodeString(getLabelNative(defaultLocale, getLabelEntryMapDefault(defaultLocale, item.labels)).name));
            url.searchParams.set('fileId', item.id);
            await navigator.clipboard.writeText(url.toString());
            alert(t('GENERAL_ALERT_COPY_URL_LABEL'));
        }
    }

    const renderItemDetailsDialog = () => {
        let itemId = showItemDetails;
        if ((itemId === undefined) || (isNullOrEmpty(itemId)) || (storageObjects.length === 0)) {
            return;
        }

        let item = storageObjects?.find((item) => (item.id === itemId)) as StorageObject;
        if ((item === undefined) || (!item)) {
            return;
        } else if (item.objectType === ObjectType.FOLDER) {
            return (
                <FolderDetailsDialog item={item}
                                     language={language}
                                     defaultLocale={defaultLocale}
                                     editMode={editModeActive}
                                     editAction={() => setFolderToEdit(item)}
                                     deleteAction={() => deleteFromDetailsDialog(item)}
                                     copyUrlAction={() => handleCopyUrl(item)}
                                     onClose={() => hideFormDetailsDialog()}/>
            );
        } else if (item.objectType === ObjectType.FILE) {
            return (
                <FileDetailsDialog item={item}
                                   language={language}
                                   defaultLocale={defaultLocale}
                                   editMode={editModeActive}
                                   editAction={() => setFileToEdit(item)}
                                   deleteAction={() => deleteFromDetailsDialog(item)}
                                   downloadAction={() => handleDownloadFile(item)}
                                   copyUrlAction={() => handleCopyUrl(item)}
                                   onClose={() => hideFormDetailsDialog()}/>
            );
        }
    };

    const renderPreviewDialog = () => {
        let itemId = showPreview;
        if ((itemId === undefined) || (isNullOrEmpty(itemId)) || (storageObjects.length === 0)) {
            return;
        }

        let item = storageObjects?.find((item) => (item.id === itemId)) as StorageObject;
        if ((item === undefined) || (!item)) {
            return;
        } else if (item.objectType === ObjectType.FILE) {
            return (
                <PreviewDialog item={item}
                               language={language}
                               defaultLocale={defaultLocale}
                               editMode={editModeActive}
                               editAction={() => setFileToEdit(item)}
                               deleteAction={() => deleteFromDetailsDialog(item)}
                               downloadAction={() => handleDownloadFile(item)}
                               onClose={() => hidePreviewDetailsDialog()}
                               previewDownloadUrl={'url'}/>
            );
        }
    };

    const renderCreateFolderDialog = () => {
        return (
            createFolderModeActive && (
                <CreateEditFolderDialog item={folderToEdit}
                                        breadcrumbs={breadcrumbs}
                                        currentPath={path}
                                        rolesDataSource={roleDataSource}
                                        parentOwnerRoles={parentOwnerRoles}
                                        parentRoles={parentRoles}
                                        parentLocales={parentLocales}
                                        language={language}
                                        defaultLocale={defaultLocale}
                                        onClose={() => setCreateFolderModeActive(false)}
                                        onSave={(storageObject, addMore) => {
                                            createFolder(getCreateFolderDTO(storageObject), addMore)
                                                .catch((error) => setDialogError({
                                                    headline: t('ERROR_FOLDER_CREATE_FAILED'),
                                                    errorDescription: setErrorDescription(error.response.data)
                                                }));
                                        }}
                />
            )
        );
    };

    const renderEditFolderDialog = () => {
        return (
            (!!folderToEdit) && (
                <CreateEditFolderDialog item={folderToEdit}
                                        breadcrumbs={breadcrumbs}
                                        currentPath={path}
                                        rolesDataSource={roleDataSource}
                                        parentOwnerRoles={parentOwnerRoles}
                                        parentRoles={parentRoles}
                                        parentLocales={parentLocales}
                                        language={language}
                                        defaultLocale={defaultLocale}
                                        onClose={() => {
                                            setFolderToEdit(null);
                                        }}
                                        onSave={(storageObject, addMore) => {
                                            editFolder(getEditFolderDTO(storageObject))
                                                .catch((error) => setDialogError({
                                                    headline: t('ERROR_FOLDER_EDIT_FAILED'),
                                                    errorDescription: setErrorDescription(error.response.data)
                                                }))
                                                .then(() => {
                                                    if (addMore) {
                                                        setCreateFolderModeActive(true);
                                                    }
                                                });
                                        }}
                />
            )
        );
    };

    const renderUploadFileDialog = () => {
        return (
            uploadFileModeActive && (
                <UploadFileDialog item={fileToEdit}
                                  breadcrumbs={breadcrumbs}
                                  currentPath={path}
                                  rolesDataSource={roleDataSource}
                                  parentOwnerRoles={parentOwnerRoles}
                                  parentRoles={parentRoles}
                                  parentLocales={parentLocales}
                                  language={language}
                                  defaultLocale={defaultLocale}
                                  disclaimerData={disclaimerOptions}
                                  labelData={existingLabelData}
                                  onClose={() => setUploadFileModeActive(false)}
                                  onSave={(storageObjectDTO, thumbnail, executeFileUpload, file, addMore) => {
                                      uploadFile(getUploadFileDTO(storageObjectDTO, thumbnail, executeFileUpload, file), addMore)
                                          .catch((error) => setDialogError({
                                              headline: t('ERROR_FILE_UPLOAD_FAILED'),
                                              errorDescription: setErrorDescription(error.response.data)
                                          }));
                                  }}
                />
            )
        );
    };

    const renderEditFileDialog = () => {
        return (
            (!!fileToEdit) && (
                <EditFileDialog item={fileToEdit}
                                currentPath={path}
                                rolesDataSource={roleDataSource}
                                parentOwnerRoles={parentOwnerRoles}
                                parentRoles={parentRoles}
                                parentLocales={parentLocales}
                                language={language}
                                defaultLocale={defaultLocale}
                                disclaimerData={disclaimerOptions}
                                labelData={existingLabelData}
                                onClose={() => {
                                    setFileToEdit(null);
                                }}
                                onSave={(storageObjectDTO, thumbnail, addMore) => {
                                    editFile(getEditFileDTO(storageObjectDTO, thumbnail))
                                        .catch((error) => setDialogError({
                                            headline: t('ERROR_FILE_EDIT_FAILED'),
                                            errorDescription: setErrorDescription(error.response.data)
                                        })).then(() => {
                                        if (addMore) {
                                            setUploadFileModeActive(true);
                                        }
                                    });
                                }}
                />
            )
        );
    };

    const renderDeleteDialog = () => {
        return (
            ((!!itemToDelete) && (!headlessDeleteAction)) && (
                <DeleteItemDialog fileTitle={itemToDelete.name}
                                  countryDataSource={getCountryDataSource(Object.keys(itemToDelete.labels))}
                                  languageDataSource={getLanguageDataSource(Object.keys(itemToDelete.labels))}
                                  localeDataSource={getLocaleDataSource(Object.keys(itemToDelete.labels))}
                                  defaultLocale={defaultLocale}
                                  isCompleteToDelete={isCompleteToDelete}
                                  countriesToDelete={countriesToDelete}
                                  languagesToDelete={languagesToDelete}
                                  localesToDelete={localesToDelete}
                                  setIsCompleteToDelete={(isComplete: boolean) => setIsCompleteToDelete(isComplete)}
                                  setCountriesToDelete={(countriesToDelete: string[]) => setCountriesToDelete(countriesToDelete)}
                                  setLanguagesToDelete={(languagesToDelete: string[]) => setLanguagesToDelete(languagesToDelete)}
                                  setLocalesToDelete={(localesToDelete: string[]) => setLocalesToDelete(localesToDelete)}
                                  onCancel={() => cancelDeleteItem()}
                                  onConfirm={() => deleteItem()
                                      .catch((error) => setDialogError({
                                          headline: t('ERROR_DELETE_FAILED'),
                                          errorDescription: setErrorDescription(error.response.data)
                                      }))}/>
            )
        );
    };

    const getDisclaimerId = (item: StorageObject | null): string => {
        return (item && (item.metadata.disclaimer !== undefined) && (!isNullOrEmpty(item.metadata.disclaimer))) ? item.metadata.disclaimer : NO_DISCLAIMER_KEY;
    }

    const executeGiveConsent = async (language: string,
                                      disclaimerId: string,
                                      fileId: string | null | undefined,
                                      isConsentGiven: boolean) => {
        await giveConsent(language, disclaimerId, fileId, isConsentGiven)
            .catch((error) => setDialogError({
                headline: t('ERROR_CONSENT_GIVE_FAILED'),
                errorDescription: setErrorDescription(error.response.data)
            }));
    }

    // const executeRevokeConsent = async (language: string,
    //                                     disclaimerId: string,
    //                                     fileId: string | null | undefined) => {
    //     await revokeConsent(language, disclaimerId, fileId)
    //         .catch((error) => setDialogError({
    //             headline: t('ERROR_CONSENT_REVOKE_FAILED'),
    //             errorDescription: setErrorDescription(error.response.data)
    //         }));
    // }

    const renderDownloadFileDialog = () => {
        if ((fileToDownload === undefined) || (!fileToDownload) || ((!fileToDownload.metadata.watermark) && (!disclaimerDialog) && (!disclaimerWatermarkDialog))) {
            return;
        }

        let itemId = fileToDownload.id;
        if ((itemId === undefined) || (isNullOrEmpty(itemId))) {
            return;
        }

        let title = t('DOWNLOAD_WATERMARKED_FILE_TITLE');
        let headline = t('DOWNLOAD_WATERMARKED_FILE_HEADLINE');
        let infoText = t('DOWNLOAD_WATERMARKED_FILE_INFO_TEXT');
        let doNotExecuteGiveConsent = (isWatermarkApplicableCheck(fileToDownload.metadata.watermark) && ((!disclaimerDialog) && (!disclaimerWatermarkDialog))) as boolean;
        let disclaimerId = '';
        if (!doNotExecuteGiveConsent) {
            disclaimerId = getDisclaimerId(fileToDownload);
            let selectedDisclaimerOption = disclaimerOptions?.filter(filteredData => (filteredData.disclaimerKey === disclaimerId));
            let isDisclaimerOptionFound = ((selectedDisclaimerOption !== undefined) && selectedDisclaimerOption && (selectedDisclaimerOption.length > 0)) as boolean;
            if (disclaimerDialog) {
                title = isDisclaimerOptionFound ? selectedDisclaimerOption[0].disclaimerType : NO_DISCLAIMER_KEY;
                headline = isDisclaimerOptionFound ? selectedDisclaimerOption[0].disclaimerText : NO_DISCLAIMER_KEY;
                infoText = t('DOWNLOAD_DISCLAIMED_FILE_INFO_TEXT');
            } else if (disclaimerWatermarkDialog) {
                title = isDisclaimerOptionFound ? selectedDisclaimerOption[0].disclaimerType : t('DOWNLOAD_WATERMARKED_DISCLAIMED_FILE_TITLE');
                headline = isDisclaimerOptionFound ? selectedDisclaimerOption[0].disclaimerText : t('DOWNLOAD_WATERMARKED_DISCLAIMED_FILE_HEADLINE');
                infoText = t('DOWNLOAD_WATERMARKED_DISCLAIMED_FILE_INFO_TEXT');
            }
        }

        return (
            <DownloadFileDialog title={title}
                                headline={headline}
                                infoText={infoText}
                                onCancel={() => {
                                    if (doNotExecuteGiveConsent) {
                                        resetFileDownload();
                                    } else {
                                        executeGiveConsent(language, disclaimerId, itemId, false)
                                            .then(() => resetFileDownload());
                                    }
                                }}
                                onConfirm={() => {
                                    if (doNotExecuteGiveConsent) {
                                        executeDownloadFile(fileToDownload);
                                    } else {
                                        executeGiveConsent(language, disclaimerId, itemId, true)
                                            .then(() => executeDownloadFile(fileToDownload));
                                    }
                                }}/>
        );
    };

    const executeDownloadFile = (item: StorageObject | null) => {
        if ((item === undefined) || (!item)) {
            return;
        }

        let itemId = item.id;
        if ((itemId === undefined) || (isNullOrEmpty(itemId))) {
            return;
        }

        if (window.dataLayer) {
            sendEventToGTM(itemId, window.dataLayer[0].ciamId);
        }

        downloadFile(item).catch((error) => setDialogError({
            headline: t('ERROR_FILE_DOWNLOAD_FAILED'),
            errorDescription: setErrorDescription(error.response.data)
        }));

        resetFileDownload();
    };

    /**
     * This function send data from download service to Google Analytics when Google Tag Manager is integrated.
     * We can create customised datalayer event/variables on Google Analytics portal.
     * Please Refer
     https://inside-docupedia.bosch.com/confluence/display/DMAS/DXF+-+Google+Analytics+integration+with+Download+Service
     */
    const sendEventToGTM = (itemId: string, ciamId: unknown) => {
        if ((itemId === undefined) || (isNullOrEmpty(itemId))) {
            return;
        }

        if (window.dataLayer) {
            window.dataLayer = window.dataLayer || [];
            return window.dataLayer.push({
                    event: 'download_file',
                    download_file_id: itemId,
                    ciam_id: ciamId,
                    download_date: new Date(),
                }
            );
        }
    };

    const handleMarkAsFavorite = async (item: StorageObject, favoriteItems: string[]) => {
        if ((!isAuthenticated) || (item === undefined) || (!item)) {
            return;
        }

        await markAsFavorite({
            resourceId: item.id,
            isFavorite: favoriteItems.includes(item.id)
        }).catch((error) => setDialogError({
            headline: t('ERROR_FOLDER_EDIT_FAILED'),
            errorDescription: setErrorDescription(error.response.data)
        }));

        navigateToPreviousPage();
    };

    const renderProgressDialog = () => {
        return (
            showProgressDialog && (
                <Dialog id={generateIdentifier()}
                        open
                        modal
                        title={t('PROGRESS_DIALOG_TITLE')}>
                    <UploadProgressIndicator/>
                </Dialog>
            )
        );
    };

    if (isAuthenticating) {
        return (
            <Layout id={generateIdentifier()}>
                <LoadingIndicator/>
            </Layout>
        );
    }

    if (dynamicConfig.DLS_AUTHENTICATION_ACTIVE ? (!isAuthenticated) : false) {
        return (
            <Layout id={generateIdentifier()}>
                <ErrorHint type={NotificationType.INFO}
                           message={t('HINT_NOT_LOGGED_IN')}/>
            </Layout>
        );
    }

    return (
        <div id={generateIdentifier()}>
            <Layout id={generateIdentifier()}
                    ref={layoutRef}>
                <SearchBarComponent itemCount={totalItemsCount}
                                    searchTermParam={searchTerm}
                                    recentlyAddedDaysParam={noOfDays}
                                    isFavoriteParam={getIsFavorite()}
                                    isPublicParam={getIsPublic()}
                                    sortOrderParam={sortOrder}
                                    objectTypeParam={objectType}
                                    fileTypesParam={fileTypes}
                                    onFilterDisable={() => {
                                        setBreadcrumbPath(BreadcrumbPaths.HOME);
                                        handleResetSearchParameters();
                                        setPopulateError(true);
                                    }}
                                    onSearchTermChange={(searchTerm) => {
                                        if ((searchTerm !== undefined) && (!isNullOrEmpty(searchTerm))) {
                                            setPage(1);
                                            setSearchTerm(searchTerm);
                                            setBreadcrumbPath(BreadcrumbPaths.FILTERED);
                                        } else {
                                            setSearchTerm(undefined);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), getIsFavorite(), getIsPublic(), sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_SEARCH);
                                        }
                                        setPopulateError(true);
                                    }}
                                    onFileTypesChange={(fileTypes) => {
                                        if (fileTypes && (fileTypes.length > 0)) {
                                            setPage(1);
                                            setFileTypes(fileTypes);
                                            setBreadcrumbPath(BreadcrumbPaths.FILTERED);
                                        } else {
                                            setFileTypes(undefined);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), getIsFavorite(), getIsPublic(), sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_FILE_TYPES);
                                        }
                                        setPopulateError(true);
                                    }}
                                    onRecentlyAddedChange={(isRecentlyAdded, noOfDays) => {
                                        if (isRecentlyAdded) {
                                            setPage(1);
                                            setNoOfDays(noOfDays);
                                            setBreadcrumbPath(BreadcrumbPaths.RECENTLY_ADDED);
                                        } else {
                                            setNoOfDays(undefined);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, isRecentlyAdded, getIsFavorite(), getIsPublic(), sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_RECENTLY_ADDED);
                                        }
                                        setPopulateError(true);
                                    }}
                                    onFavoriteChange={(isFavorite) => {
                                        if (isFavorite) {
                                            setPage(1);
                                            setIsFavorite(isFavorite.toString());
                                            setBreadcrumbPath(BreadcrumbPaths.FAVORITES);
                                        } else {
                                            setIsFavorite(undefined);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), isFavorite, getIsPublic(), sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_IS_FAVORITE);
                                        }
                                        setPopulateError(true);
                                    }}
                                    onPublicChange={(isPublic) => {
                                        if (isPublic) {
                                            setPage(1);
                                            setIsPublic(isPublic.toString());
                                            setBreadcrumbPath(BreadcrumbPaths.PUBLIC_ACCESS);
                                        } else {
                                            setIsPublic(undefined);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), getIsFavorite(), isPublic, sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_IS_PUBLIC);
                                        }
                                        setPopulateError(true);
                                    }}
                                    onSortItemsChange={(sortOrder) => {
                                        setSortOrder(sortOrder);
                                        setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), getIsFavorite(), getIsPublic(), sortOrder, objectType, fileTypes));
                                        setPopulateError(true);
                                    }}
                                    onObjectTypeChange={(objectType) => {
                                        if ((objectType !== undefined) && objectType && (objectType.toLowerCase() !== TYPE_ALL)) {
                                            setObjectType(objectType);
                                            setBreadcrumbPath(BreadcrumbPaths.FILTERED);
                                        } else {
                                            setObjectType(objectType);
                                            setBreadcrumbPath(getBreadcrumbState(searchTerm, getIsRecentlyAdded(), getIsFavorite(), getIsPublic(), sortOrder, objectType, fileTypes));
                                            handleDeleteSearchParameter(PARAM_TYPE);
                                        }
                                        setPopulateError(true);
                                    }}
                                    mainAction={
                                        editModeActive && (
                                            <AddItemMenu
                                                onCreateFolderClicked={() => setCreateFolderModeActive(true)}
                                                onUploadFileClicked={() => setUploadFileModeActive(true)}
                                            />
                                        )
                                    }/>
                <hr id={generateIdentifier()}/>
                <Breadcrumb breadcrumbs={breadcrumbs}
                            breadcrumbPath={breadcrumbPath}
                            onPathClicked={(path) => {
                                setPath(decodeURI(path));
                                setPage(1);
                                setPopulateError(true);
                            }}/>
                <ItemsViewWrapper id={generateIdentifier()}>
                    <ItemsHeader page={page}
                                 pageCount={pageCount}
                                 itemsPerPageCount={itemsPerPageCount}
                                 itemsCount={storageObjects.length}
                                 totalItemsCount={totalItemsCount}
                                 itemLayout={itemLayout}
                                 onPageChange={setPage}
                                 onLayoutChange={setItemLayout}/>
                    {renderItems()}
                    <ItemsFooter page={page}
                                 pageCount={pageCount}
                                 onPageChange={setPage}/>
                </ItemsViewWrapper>
            </Layout>
            {renderItemDetailsDialog()}
            {renderPreviewDialog()}
            {renderCreateFolderDialog()}
            {renderEditFolderDialog()}
            {renderUploadFileDialog()}
            {renderEditFileDialog()}
            {renderDeleteDialog()}
            {renderProgressDialog()}
            {renderErrorDialog(!!dialogError, dialogError?.headline, dialogError?.errorDescription)}
            {renderDownloadFileDialog()}
        </div>
    );
};
